/**
 * Runtime bridge: dashboard / macro-week use the same menu stack as MenuFlowProvider
 * (`menu` vs `foodMenu`) based on `menu_page_style` type3/type4.
 */
import { useMemo } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useIsFoodMenuTabFlow } from '../config';
import { isFoodMenuTabFlow } from '../config/appConfig';
import type { DayConfig } from '../subscription/types';
import {
  useMenuDate as useClassicMenuDate,
  useMenuState as useClassicMenuState,
  syncMenuCacheToStore as syncClassicMenuCacheToStore,
  useMenuStore as useClassicMenuStore,
  menuKeys as classicMenuKeys,
  fetchMenu as fetchClassicMenu,
  isMenuDateSlotDivergentFromServer,
  mergeMenuBuildersBox1View,
  computeMacrosFromBuilders,
  type MenuForDateQueryOptions as ClassicMenuForDateQueryOptions,
} from '../menu';
import {
  useMenuDate as useFoodMenuDate,
  useMenuState as useFoodMenuState,
  syncMenuCacheToStore as syncFoodMenuCacheToStore,
  useMenuStore as useFoodMenuStore,
  menuKeys as foodMenuKeys,
  fetchMenu as fetchFoodMenu,
  type MenuForDateQueryOptions as FoodMenuForDateQueryOptions,
} from '../foodMenu';

export type ActiveMenuStack = 'menu' | 'foodMenu';

export function getActiveMenuStack(): ActiveMenuStack {
  return isFoodMenuTabFlow() ? 'foodMenu' : 'menu';
}

export function useActiveMenuStack(): ActiveMenuStack {
  return useIsFoodMenuTabFlow() ? 'foodMenu' : 'menu';
}

export function activeMenuDayKey(customer_id: number, date_ymd: string, is_double_box: boolean) {
  return getActiveMenuStack() === 'foodMenu'
    ? foodMenuKeys.day(customer_id, date_ymd, is_double_box)
    : classicMenuKeys.day(customer_id, date_ymd, is_double_box);
}

export function activeFetchMenu(customer_id: number, date: string, is_double_box: boolean) {
  return getActiveMenuStack() === 'foodMenu'
    ? fetchFoodMenu(customer_id, date, is_double_box)
    : fetchClassicMenu(customer_id, date, is_double_box);
}

export function syncActiveMenuCacheToStore(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  day_config_map: Record<string, DayConfig>,
): void {
  if (getActiveMenuStack() === 'foodMenu') {
    syncFoodMenuCacheToStore(query_client, customer_id, date, day_config_map);
    return;
  }
  syncClassicMenuCacheToStore(query_client, customer_id, date, day_config_map);
}

export type ActiveMenuForDateQueryOptions = ClassicMenuForDateQueryOptions & FoodMenuForDateQueryOptions;

export function useActiveMenuForDateQuery(
  date: string,
  isDoubleBox?: boolean,
  options?: ActiveMenuForDateQueryOptions,
) {
  const is_food = useIsFoodMenuTabFlow();
  const classic = useClassicMenuDate(date, isDoubleBox, {
    ...options,
    query_enabled: options?.query_enabled !== false && !is_food,
  });
  const food = useFoodMenuDate(date, isDoubleBox, {
    ...options,
    query_enabled: options?.query_enabled !== false && is_food,
  });
  return is_food ? food : classic;
}

export function useActiveMenuState() {
  const is_food = useIsFoodMenuTabFlow();
  const classic = useClassicMenuState();
  const food = useFoodMenuState();
  return is_food ? food : classic;
}

/** Zustand selector against the active menu store (both stores subscribed; result follows stack). */
export function useActiveMenuStoreSelector<T>(
  selector: (state: ReturnType<typeof useClassicMenuStore.getState>) => T,
): T {
  const is_food = useIsFoodMenuTabFlow();
  const classic = useClassicMenuStore(selector);
  const food = useFoodMenuStore(selector as (state: ReturnType<typeof useFoodMenuStore.getState>) => T);
  return (is_food ? food : classic) as T;
}

export function useActiveMenuDayKeyFactory() {
  const stack = useActiveMenuStack();
  return useMemo(
    () => (customer_id: number, date_ymd: string, is_double_box: boolean) =>
      stack === 'foodMenu'
        ? foodMenuKeys.day(customer_id, date_ymd, is_double_box)
        : classicMenuKeys.day(customer_id, date_ymd, is_double_box),
    [stack],
  );
}

export { isMenuDateSlotDivergentFromServer, mergeMenuBuildersBox1View, computeMacrosFromBuilders };
